import { FormEvent, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { errorMessage, fetchJobs } from './api';

const providerLabel: Record<string, string> = {
  greenhouse: 'Greenhouse',
  lever: 'Lever',
  ashby: 'Ashby',
};

function companySlug(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\b(srl|spa|inc|ltd|llc|gmbh|company|italia)\b/g, ' ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export default function CompetitorJobs({ pageName }: { pageName: string }) {
  const [slug, setSlug] = useState(() => companySlug(pageName));
  const [visible, setVisible] = useState(8);
  const jobs = useMutation({
    mutationFn: () => fetchJobs(slug),
    onSuccess: () => setVisible(8),
  });

  const leadingDepartments = useMemo(() => {
    const counts = new Map<string, number>();
    (jobs.data?.postings ?? []).forEach((posting) => {
      if (posting.department) counts.set(posting.department, (counts.get(posting.department) ?? 0) + 1);
    });
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3);
  }, [jobs.data]);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (slug.trim()) jobs.mutate();
  }

  return <details className="presence-disclosure jobs-block">
    <summary><span><strong>Assunzioni</strong><small>Posizioni aperte e direzioni di crescita</small></span><span aria-hidden="true">+</span></summary>
    <div className="presence-disclosure-body">
    <p className="presence-intro">Le posizioni aperte possono anticipare nuovi mercati, prodotti o segmenti commerciali.</p>
    <button type="button" className="btn-secondary jobs-run" disabled={jobs.isPending || !slug.trim()} onClick={() => jobs.mutate()}>{jobs.isPending ? 'Cerco posizioni…' : 'Cerca posizioni'}</button>
    <details className="jobs-source-settings"><summary>Correggi sorgente Careers</summary><form className="jobs-search" onSubmit={submit}><label className="field"><span>Identificativo azienda</span><input type="text" value={slug} onChange={(event) => { setSlug(event.target.value); jobs.reset(); }} placeholder="es. notion" autoCapitalize="none" spellCheck={false} /></label><button type="submit" className="btn-secondary" disabled={jobs.isPending || !slug.trim()}>Applica e cerca</button></form><p className="jobs-help">Usa l’identificativo presente nell’indirizzo Greenhouse, Lever o Ashby dell’azienda.</p></details>

    {jobs.isError && <div className="notice notice-error" role="alert"><p>{errorMessage(jobs.error)}</p><button type="button" className="btn-secondary" onClick={() => jobs.mutate()}>Riprova</button></div>}
    {jobs.isSuccess && jobs.data.count === 0 && <section className="empty empty-compact jobs-empty"><h4>Nessuna board pubblica trovata</h4><p>Controlla lo slug oppure verifica dal sito dell’azienda quale piattaforma Careers utilizza.</p></section>}
    {jobs.isSuccess && jobs.data.count > 0 && <>
      <div className="jobs-insight"><div><strong>{jobs.data.count} posizioni aperte</strong><span>da {providerLabel[jobs.data.postings[0]?.provider] ?? jobs.data.postings[0]?.provider ?? 'ATS pubblico'}</span></div>{leadingDepartments.length > 0 && <p>{jobs.data.count > jobs.data.postings.length ? `Tra le prime ${jobs.data.postings.length}` : 'Più presenti'}: {leadingDepartments.map(([department, count]) => `${department} (${count})`).join(' · ')}</p>}</div>
      <div className="jobs-list">{jobs.data.postings.slice(0, visible).map((posting, index) => <article className="job-row" key={`${posting.url ?? posting.title}-${index}`}>
        <div><h4>{posting.title ?? 'Posizione senza titolo'}</h4><p>{[posting.department, posting.location].filter(Boolean).join(' · ') || 'Dettagli non disponibili'}</p></div>
        {posting.url && <a href={posting.url} target="_blank" rel="noreferrer">Apri ↗</a>}
      </article>)}</div>
      {visible < jobs.data.postings.length && <button type="button" className="btn-secondary jobs-more" onClick={() => setVisible(jobs.data!.postings.length)}>{jobs.data.count > jobs.data.postings.length ? `Mostra le prime ${jobs.data.postings.length}` : 'Mostra tutte le posizioni'}</button>}
      <p className="data-note">Segnale estratto on-demand da board Careers pubbliche. Non indica da solo una decisione strategica già presa.{jobs.data.count > jobs.data.postings.length ? ` La lista mostra le prime ${jobs.data.postings.length} posizioni.` : ''}</p>
    </>}
    </div>
  </details>;
}
